import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalcSteps } from "@/components/calc/CalcSteps";
import { ResultsTable, CALC_PRINT_CSS } from "@/components/calc/ResultsTable";
import { ExportBar } from "@/components/calc/ExportBar";
import { ScenarioCompare } from "@/components/calc/ScenarioCompare";
import { CalcHistory } from "@/components/calc/CalcHistory";
import { BalanceLine } from "@/components/calc/BalanceLine";
import { recordCalculation } from "@/lib/calc/history";
import { stepsToText } from "@/lib/calc/steps";
import { compareScenarios } from "@/lib/calc/scenarios";
import { tableToCsv, tableToTsv } from "@/lib/calc/table";
import { parseNumber } from "./numberInput";
import { formatFullDate, paymentDate } from "./paymentDates";
import { loanColumns, loanHistorySummary, loanScenarioMetrics, loanScenarios, loanSteps, type LoanInputs } from "./loanExtras";
import type { LoanResult } from "./loan";
import type { YMD } from "./dates";

/**
 * Everything under the Loan Calculator's result: working, scenario comparison, schedule table with export, history.
 * Loaded on demand (React.lazy in LoanCalculator) so the table, export and scenario code stay out of the tool's first load.
 */
export default function LoanExtras({ inputs, res, first, money, gate }: {
  inputs: LoanInputs; res: LoanResult; first: YMD; money: (n: number) => string; gate: () => Promise<boolean>;
}) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [extraB, setExtraB] = useState("100");
  const [extraC, setExtraC] = useState("250");
  const steps = useMemo(() => loanSteps(inputs, res, first, money), [inputs, res, first, money]);
  const columns = useMemo(() => loanColumns(first, money), [first, money]);
  const showScenarios = inputs.method === "reducing";
  const scenarios = useMemo(
    () => (showScenarios ? loanScenarios(inputs, [parseNumber(extraB) ?? 0, parseNumber(extraC) ?? 0], first, money) : []),
    [showScenarios, inputs, extraB, extraC, first, money],
  );
  const metrics = useMemo(() => loanScenarioMetrics(money), [money]);

  // The visit's history: one line per settled set of inputs (not one per keystroke).
  const summary = loanHistorySummary(inputs, res, money);
  useEffect(() => {
    const t = setTimeout(() => recordCalculation("loan-calculator", summary), 800);
    return () => clearTimeout(t);
  }, [summary]);

  const csv = tableToCsv(res.rows, columns);
  const scheduleText = tableToTsv(res.rows, columns);
  const reportText = [
    "Loan calculation", summary, "", "How this was calculated", stepsToText(steps), "",
    ...(showScenarios ? ["Scenario comparison", ...compareScenarios(scenarios, metrics).map((r) => `${r.label}: ${r.cells.join(" | ")}`), ""] : []),
    "Amortization schedule", scheduleText, "", "Calculated with FreeDailyPro.com",
  ].join("\n");

  return (
    <div className="mt-8 space-y-6 calc-print-area">
      <style>{CALC_PRINT_CSS}</style>
      <CalcSteps steps={steps} />
      <BalanceLine points={[{ n: 0, balance: inputs.principal }, ...res.rows.map((r) => ({ n: r.month, balance: r.balance }))]} money={money} startLabel={formatFullDate(first)} endLabel={formatFullDate(paymentDate(first, res.rows.length - 1))} />

      {showScenarios && (
        <section aria-label="Compare extra payments" className="space-y-3">
          <h3 className="text-base font-semibold">Compare extra payments</h3>
          <div className="no-print print:hidden grid grid-cols-2 gap-3 max-w-md">
            <div><Label htmlFor="ln-scn-b">Scenario 2: extra each month</Label><Input id="ln-scn-b" inputMode="decimal" value={extraB} onChange={(e) => setExtraB(e.target.value)} /></div>
            <div><Label htmlFor="ln-scn-c">Scenario 3: extra each month</Label><Input id="ln-scn-c" inputMode="decimal" value={extraC} onChange={(e) => setExtraC(e.target.value)} /></div>
          </div>
          <ScenarioCompare scenarios={scenarios} metrics={metrics} caption="Loan scenarios with different extra monthly payments" />
        </section>
      )}

      <section aria-label="Amortization schedule" className="space-y-3">
        <div className="no-print print:hidden flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => setShowSchedule((s) => !s)} aria-expanded={showSchedule} className="gap-2">
            {showSchedule ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
            {showSchedule ? "Hide" : "Show"} full amortization schedule ({res.rows.length} {res.rows.length === 1 ? "payment" : "payments"})
          </Button>
        </div>
        <ExportBar gate={gate} text={reportText} csv={csv} filename="loan-schedule.csv" label="report" />
        <div className={showSchedule ? "" : "hidden print:block"}>
          <ResultsTable caption="Loan amortization schedule" rows={res.rows} columns={columns} rowKey={(r) => r.month} />
        </div>
      </section>

      <CalcHistory tool="loan-calculator" />
    </div>
  );
}
